import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AlertTriangle, Droplets, UserPlus, MessageSquarePlus, FlaskConical } from "lucide-react";
import { PageHeader } from "@/components/AdminLayout";
import { OrderStatusBadge, PaymentBadge, StockBadge } from "@/components/Badges";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const Stat = ({ label, value, sub, testId, accent = "border-t-emerald-500" }) => (
  <div className={`rounded-lg border border-border border-t-2 ${accent} bg-card p-6 rise`} data-testid={testId}>
    <p className="eyebrow text-muted-foreground">{label}</p>
    <p className="mt-3 font-mono-data text-3xl">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Alert = ({ icon: Icon, to, text, color, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-center gap-3 rounded-md border border-border bg-background/40 px-4 py-3 text-sm transition-colors hover:bg-secondary">
    <Icon className={`h-4 w-4 ${color}`} /> {text}
  </Link>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!d) return <p className="text-muted-foreground">Loading…</p>;

  const alerts = [
    d.pending_clients > 0 && { icon: UserPlus, to: "/admin/clients", text: `${d.pending_clients} client(s) waiting for approval`, color: "text-amber-400", testId: "alert-pending-clients" },
    d.recon_pending > 0 && { icon: FlaskConical, to: "/admin/reconstitution", text: `${d.recon_pending} vial line(s) to reconstitute`, color: "text-emerald-400", testId: "alert-recon-pending" },
    d.new_requests > 0 && { icon: MessageSquarePlus, to: "/admin/requests", text: `${d.new_requests} new peptide request(s)`, color: "text-sky-400", testId: "alert-new-requests" },
    d.bac_water_low && { icon: Droplets, to: "/admin/inventory", text: `BAC water low: ${d.bac_water_stock_ml} mL left`, color: "text-red-400", testId: "alert-bac-low" },
  ].filter(Boolean);

  return (
    <div>
      <PageHeader eyebrow="Overview" title="Dashboard" />
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total sales" value={money(d.total_sales)} sub={`${d.order_count} orders`} testId="stat-total-sales" />
        <Stat label="This month" value={money(d.month_sales)} testId="stat-month-sales" accent="border-t-sky-500" />
        <Stat label="Collected" value={money(d.collected)} testId="stat-collected" />
        <Stat label="Outstanding" value={money(d.outstanding)} sub={`${d.pending_orders} pending orders`} testId="stat-outstanding" accent="border-t-amber-500" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Product profit" value={money(d.product_profit)} sub={`${money(d.product_revenue)} sales − ${money(d.product_cost)} cost`} testId="stat-profit" />
        <Stat label="Profit margin" value={d.margin_pct == null ? "—" : `${d.margin_pct}%`} sub={d.products_missing_cost ? `${d.products_missing_cost} product(s) have no cost set yet` : "Based on what each item cost you"} testId="stat-margin" accent="border-t-sky-500" />
        <Stat label="Profit this month" value={money(d.month_profit)} testId="stat-month-profit" />
        <Stat label="Recon fees · discounts" value={money(d.recon_income)} sub={`${money(d.discounts_given)} given in discount codes`} testId="stat-recon-discounts" accent="border-t-amber-500" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-6 lg:col-span-2" data-testid="sales-chart">
          <p className="mb-4 text-sm font-medium">Sales · last 30 days</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.sales_by_day}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E3446" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v}`} width={50} />
                <Tooltip contentStyle={{ background: "#0E1A24", border: "1px solid #1E3446", borderRadius: 6 }} formatter={(v) => money(v)} />
                <Area type="monotone" dataKey="total" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="mb-3 text-sm font-medium">Needs attention</p>
            <div className="space-y-2">
              {alerts.length ? alerts.map((a) => <Alert key={a.testId} {...a} />) : <p className="text-sm text-muted-foreground">All clear.</p>}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-6" data-testid="payment-split">
            <p className="mb-3 text-sm font-medium">Sales by payment</p>
            <div className="space-y-2 font-mono-data text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Cash</span>{money(d.payment_split.cash)}</div>
              <div className="flex justify-between"><span className="text-orange-400">Bitcoin</span>{money(d.payment_split.bitcoin)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-6" data-testid="top-products">
          <p className="mb-4 text-sm font-medium">Top products</p>
          {d.top_products.length === 0 && <p className="text-sm text-muted-foreground">No sales yet.</p>}
          {d.top_products.map((p) => (
            <div key={p.name} className="flex justify-between border-b border-border/60 py-2 text-sm last:border-0">
              <span>{p.name} <span className="font-mono-data text-muted-foreground">×{p.quantity}</span></span>
              <span className="text-right font-mono-data">{money(p.revenue)}<span className="block text-xs text-emerald-400">{money(p.profit)} profit</span></span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-border bg-card p-6" data-testid="low-stock-list">
          <p className="mb-4 text-sm font-medium">Low / out of stock</p>
          {d.low_stock.length === 0 && <p className="text-sm text-muted-foreground">Everything is stocked.</p>}
          {d.low_stock.map((p) => (
            <div key={p.id} className="flex items-center justify-between border-b border-border/60 py-2 text-sm last:border-0">
              <span>{p.name} <span className="font-mono-data text-muted-foreground">{p.stock} left</span></span>
              <StockBadge status={p.stock_status} />
            </div>
          ))}
          <p className="mt-4 font-mono-data text-xs text-sky-400">BAC water for reconstitution: {d.bac_water_stock_ml} mL</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-6" data-testid="recent-orders">
          <div className="mb-4 flex justify-between text-sm font-medium">Recent orders <Link to="/admin/orders" className="text-emerald-400">View all</Link></div>
          {d.recent_orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
          {d.recent_orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-2 border-b border-border/60 py-2 text-sm last:border-0">
              <div>
                <p className="font-mono-data">{o.order_number}</p>
                <p className="text-xs text-muted-foreground">{o.client_name} · {fmtDate(o.created_at)}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-mono-data">{money(o.total)}</span>
                <div className="flex gap-1"><OrderStatusBadge status={o.status} /><PaymentBadge status={o.payment_status} /></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
